import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";

import { useAuth } from "../contexts/AuthContext";
import ThreadSidebar from "./ThreadSidebar";
import NewThreadModal from "./NewThreadModal";
import Message from "./Message";
import Navbar from "./Navbar";
import ChatInput from "./ChatInput";

const isInternalNetwork = () => {
  const hostname = window.location.hostname;
  return hostname === "testreact.home-k8s.lab";
};

const API_URL = isInternalNetwork()
  ? process.env.REACT_APP_API_URL_INTERNAL
  : process.env.REACT_APP_API_URL_EXTERNAL;

const Chat = () => {
  const { token, user, logout } = useAuth();
  const [message, setMessage] = useState("");
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [isNewThreadModalOpen, setIsNewThreadModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const eventSourceRef = useRef(null);

  const messagesEndRef = useRef(null);

  const handleError = useCallback(
    (error) => {
      console.error("Error occurred:", error);
      if (error.response?.status === 401) {
        logout();
      } else {
        setMessage(error.response?.data?.message || "An error occurred");
      }
    },
    [logout]
  );

  // Add handleCreateThread function
  const handleCreateThread = async (title) => {
    if (!token || !title.trim()) return;

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API_URL}/api/threads`,
        { title, initialMessage: "Start of conversation" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsNewThreadModalOpen(false);
      await fetchThreads();
      setActiveThreadId(response.data.threadId);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Add handleUpdateThread function
  const handleUpdateThread = async (threadId, newTitle) => {
    if (!token || !threadId || !newTitle.trim()) return;

    try {
      await axios.patch(
        `${API_URL}/api/threads/${threadId}`,
        { title: newTitle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchThreads();
    } catch (error) {
      handleError(error);
    }
  };

  // Clean up EventSource on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // Rest of the code remains exactly the same as in the previous version...

  useEffect(() => {
    if (activeThreadId && !isLoading) {
      fetchThreadMessages(activeThreadId);
    }
  }, [activeThreadId, isLoading]);

  const fetchThreads = async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${API_URL}/api/threads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setThreads(response.data);
    } catch (error) {
      handleError(error);
    }
  };

  const scrollToBottom = () => {
    const behavior = isSubmitting ? "auto" : "smooth";
    messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isSubmitting]);

  const fetchThreadMessages = useCallback(
    async (threadId) => {
      if (!threadId || !token) return;

      try {
        const response = await axios.get(
          `${API_URL}/api/threads/${threadId}/messages`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const sortedMessages = response.data.messages.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
        setMessages(sortedMessages);
      } catch (error) {
        handleError(error);
      }
    },
    [token, handleError]
  );

  useEffect(() => {
    const initializeChat = async () => {
      if (!token) return;

      try {
        setIsLoading(true);
        const response = await axios.get(`${API_URL}/api/threads`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setThreads(response.data);

        if (response.data.length > 0) {
          const firstThreadId = response.data[0]._id;
          setActiveThreadId(firstThreadId);
          await fetchThreadMessages(firstThreadId);
        }
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeChat();
  }, [token, fetchThreadMessages, handleError]);

  const handleSubmit = async ({ text, imageId }) => {
    if ((!text.trim() && !imageId) || isSubmitting) {
      return;
    }

    // 关闭已存在的 EventSource
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      setIsSubmitting(true);
      let currentThreadId = activeThreadId;

      // 如果没有活动线程，创建新线程
      if (!currentThreadId) {
        const response = await axios.post(
          `${API_URL}/api/threads`,
          {
            title: text.trim().slice(0, 20) + (text.length > 20 ? "..." : ""),
            initialMessage: text,
            imageId,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        currentThreadId = response.data.threadId;
        await fetchThreads();
        setActiveThreadId(currentThreadId);
      }

      // 创建消息对象用于立即显示
      const tempMessage = {
        _id: Date.now().toString(),
        text,
        createdAt: new Date().toISOString(),
        processingStatus: "processing",
        llmResponse: {
          response: "",
          model: "processing...",
          totalDuration: 0,
          evalDuration: 0,
          promptEvalCount: 0,
          evalCount: 0,
        },
      };

      // 添加临时消息到状态
      setMessages((prev) => [...prev, tempMessage]);

      // 发送消息请求
      const response = await fetch(
        `${API_URL}/api/threads/${currentThreadId}/messages`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "text/event-stream",
          },
          body: JSON.stringify({ text, imageId }),
        }
      );

      // 创建 ReadableStream 读取器
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let messageId = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log("Received SSE data:", data);

              if (data.messageId && !messageId) {
                messageId = data.messageId;
                setMessages((prevMessages) =>
                  prevMessages.map((msg) =>
                    msg._id === tempMessage._id
                      ? { ...msg, _id: data.messageId }
                      : msg
                  )
                );
                setStreamingMessageId(data.messageId);
              }

              if (data.done) {
                setMessages((prevMessages) =>
                  prevMessages.map((msg) =>
                    msg._id === messageId
                      ? {
                          ...msg,
                          processingStatus: "success",
                          llmResponse: {
                            ...msg.llmResponse,
                            model: data.model || "llama2", // 确保从后端获取model名称
                            totalDuration: data.totalDuration,
                            evalDuration: data.evalDuration,
                            promptEvalCount: data.promptEvalCount,
                            evalCount: data.evalCount,
                          },
                        }
                      : msg
                  )
                );
                setStreamingMessageId(null);
                break;
              }

              // 更新消息内容
              setMessages((prevMessages) =>
                prevMessages.map((msg) =>
                  msg._id === (messageId || tempMessage._id)
                    ? {
                        ...msg,
                        llmResponse: {
                          ...msg.llmResponse,
                          response:
                            (msg.llmResponse.response || "") +
                            (data.content || ""),
                          // 如果有model信息也立即更新
                          ...(data.model && { model: data.model }),
                        },
                      }
                    : msg
                )
              );
              scrollToBottom();
            } catch (error) {
              console.error("Error parsing SSE data:", error);
            }
          }
        }
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <span>Loading chat...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <Navbar
        user={user}
        logout={logout}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        isSidebarOpen={isSidebarOpen}
      />

      <ThreadSidebar
        threads={threads}
        activeThreadId={activeThreadId}
        onThreadSelect={setActiveThreadId}
        onCreateThread={() => setIsNewThreadModalOpen(true)}
        onUpdateThread={handleUpdateThread}
        onDeleteThread={async (threadId) => {
          await fetchThreads();
          if (activeThreadId === threadId) {
            setActiveThreadId(threads[0]?._id);
          }
        }}
        isOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="h-screen pt-20 pb-24 flex flex-col md:ml-64">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-4">
            {message && (
              <div className="mb-4 p-3 bg-yellow-900/50 text-yellow-100 rounded-lg border border-yellow-700/50">
                {message}
              </div>
            )}

            <div className="space-y-4">
              {messages.length > 0 ? (
                messages.map((msg) => (
                  <Message
                    key={msg._id}
                    message={msg}
                    token={token}
                    API_URL={API_URL}
                    threadId={activeThreadId}
                    onDelete={(messageId) => {
                      setMessages((prevMessages) =>
                        prevMessages.filter((m) => m._id !== messageId)
                      );
                      fetchThreads();
                    }}
                    expandedMessages={expandedMessages}
                    setExpandedMessages={setExpandedMessages}
                    handleError={handleError}
                    streamingMessageId={streamingMessageId}
                    onStreamComplete={() => setStreamingMessageId(null)}
                  />
                ))
              ) : (
                <p className="text-gray-400 italic text-center py-8">
                  No messages yet
                </p>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>

        <ChatInput
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          token={token}
          API_URL={API_URL}
          placeholder={
            threads.length === 0
              ? "Type something to start a new chat..."
              : "Type your message..."
          }
        />
      </div>

      <NewThreadModal
        isOpen={isNewThreadModalOpen}
        onClose={() => setIsNewThreadModalOpen(false)}
        onSubmit={handleCreateThread}
      />
    </div>
  );
};

export default Chat;
